import React, { useState } from "react";
import {
  Input,
  Label,
  FormGroup,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import Icon from "@mdi/react";
import { mdiEyeOff, mdiEye } from "@mdi/js";
import "../../STYLE/BaseInput.css";

interface BaseInputProps {
  label?: string;
  accept?: string;
  name: string;
  type: "text" | "email" | "password" | "number" | "file" | "textarea";
  placeholder?: string;
  value?: string | string[];  
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  error?: string;
  touched?: boolean;
  required?: boolean;
  className?: string;
  showPasswordToggle?: boolean;
  maxLength?: number | undefined;
  ref?: React.RefObject<HTMLInputElement>;
  tooltip?: boolean;
  tooltipIcon?: string;
  tooltipIconColor?: string;
  tooltipText?: string;
  multiple?: boolean;
}

const BaseInput: React.FC<BaseInputProps> = ({
  required,
  accept,
  label,
  name,
  type,
  placeholder,
  value,
  onChange,
  tooltip,
  tooltipIcon,
  tooltipIconColor,
  tooltipText,
  onBlur,
  error,
  touched,
  className,
  showPasswordToggle,
  maxLength,
  multiple,
  ref,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleToggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      ".",
    ];
    if (
      type === "number" &&
      maxLength &&
      String(value)?.toString().length >= maxLength
    ) {
      const isEditing = allowedKeys.includes(event.key);
      if (!isEditing) {
        event.preventDefault();
      }
    }
  };

  return (
    <div className={className}>
      {label && (
        <>
          <Label for={name}>
            {label}
            {required && <span className="text-danger">*</span>}
          </Label>
          {tooltip && (
            <i
              className={`mdi mdi-${tooltipIcon} ms-1 text-${tooltipIconColor} cursor-pointer tooltip-container`}
            >
              <span className="tooltip-text bottom-5 mt-4">{tooltipText}</span>
            </i>
          )}
        </>
      )}

      <InputGroup>
        {type === "textarea" ? (
          <textarea
            className="form-control"
            name={name}
            placeholder={placeholder}
            value={value as string}
            onChange={onChange}
            onBlur={onBlur}
          />
        ) : (
          <Input
            type={type === "password" && showPassword ? "text" : type}
            maxLength={type !== "number" ? maxLength : undefined}
            accept={accept}
            name={name}
            placeholder={placeholder}
            value={value as string}
            multiple={type === "file" ? multiple : undefined}
            onKeyDown={handleKeyDown}
            onChange={onChange}
            onBlur={onBlur}
            invalid={!!(error && touched)}
          />
        )}

        {showPasswordToggle && type === "password" && (
          <InputGroupText
            onClick={handleToggleShowPassword}
            className="cursor-pointer"
          >
            <Icon
              path={showPassword ? mdiEye : mdiEyeOff}
              size={0.6}
              className="text-dark"
            />
          </InputGroupText>
        )}
      </InputGroup>
      {touched && error && <span className="text-danger errtxt">{error}</span>}
    </div>
  );
};

export default BaseInput;
